import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { codeBuilderApiRef } from '../../api';
import type { GithubIssueSummary } from '../../api';
import type {
  CodeBuilderFormData,
  CodeBuilderSelectedIssue,
  CodeBuilderUiOptions,
} from './types';

// Kinds that are never repositories, hidden unless `repoKinds` opts them back in.
const EXCLUDED_KINDS = new Set(['group', 'location', 'user']);

// Group order in the picker; anything else falls after these, alphabetically.
const KIND_ORDER = [
  'tenant',
  'domain',
  'system',
  'component',
  'template',
  'resource',
];

function kindRank(kind: string): number {
  const index = KIND_ORDER.indexOf(kind.toLowerCase());
  return index === -1 ? KIND_ORDER.length : index;
}

export const CodeBuilderConfigPanel = (props: {
  formData: CodeBuilderFormData;
  uiOptions: CodeBuilderUiOptions;
  onChange(patch: Partial<CodeBuilderFormData>): void;
}) => {
  const { formData, uiOptions, onChange } = props;
  const catalogApi = useApi(catalogApiRef);
  const codeBuilderApi = useApi(codeBuilderApiRef);

  // By default every catalog kind except Group/Location/User is selectable,
  // grouped by kind in the picker. `repoKinds` overrides both (exact list, no
  // exclusions).
  const repoKinds = uiOptions.repoKinds?.length ? uiOptions.repoKinds : undefined;
  const repoKindsKey = repoKinds?.join(',') ?? '*';

  const [repos, setRepos] = useState<Entity[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [reposError, setReposError] = useState<string>();

  const [issues, setIssues] = useState<GithubIssueSummary[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setReposLoading(true);
    setReposError(undefined);
    catalogApi
      .getEntities({
        filter: repoKinds ? repoKinds.map(kind => ({ kind })) : undefined,
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.annotations',
        ],
      })
      .then(response => {
        if (cancelled) {
          return;
        }
        // Pre-sort by kind rank, then label: MUI's `groupBy` needs contiguous
        // groups and renders them in options order. Drop Group/Location/User
        // unless the operator explicitly asked for them.
        setRepos(
          response.items
            .filter(
              entity =>
                Boolean(repoKinds) ||
                !EXCLUDED_KINDS.has(entity.kind.toLowerCase()),
            )
            .sort(
              (a, b) =>
                kindRank(a.kind) - kindRank(b.kind) ||
                a.kind.localeCompare(b.kind) ||
                entityLabel(a).localeCompare(entityLabel(b)),
            ),
        );
      })
      .catch(error => {
        if (!cancelled) {
          setReposError(
            error instanceof Error ? error.message : String(error),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReposLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, repoKindsKey]);

  useEffect(() => {
    const repoEntityRef = formData.repoEntityRef;
    if (!repoEntityRef) {
      setIssues([]);
      setIssuesError(undefined);
      return undefined;
    }
    let cancelled = false;
    setIssuesLoading(true);
    setIssuesError(undefined);
    codeBuilderApi
      .listIssues(repoEntityRef)
      .then(result => {
        if (!cancelled) {
          setIssues(result.issues);
        }
      })
      .catch(error => {
        if (!cancelled) {
          setIssues([]);
          setIssuesError(
            error instanceof Error ? error.message : String(error),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIssuesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [codeBuilderApi, formData.repoEntityRef]);

  const selectedRepo = useMemo(
    () =>
      repos.find(entity => stringifyEntityRef(entity) === formData.repoEntityRef) ??
      null,
    [repos, formData.repoEntityRef],
  );

  const selectedIssues: CodeBuilderSelectedIssue[] = useMemo(
    () => formData.issues ?? [],
    [formData.issues],
  );
  const selectedIssueOptions = useMemo(() => {
    const byNumber = new Map(issues.map(issue => [issue.number, issue]));
    return selectedIssues.map(
      issue =>
        byNumber.get(issue.number) ??
        ({
          number: issue.number,
          title: issue.title,
          state: 'unknown',
          labels: [],
          assignees: [],
          author: 'unknown',
          createdAt: '',
          updatedAt: '',
          url: issue.url ?? '',
        } as GithubIssueSummary),
    );
  }, [issues, selectedIssues]);

  const onRepoChange = (entity: Entity | null) => {
    onChange({
      repoEntityRef: entity ? stringifyEntityRef(entity) : undefined,
      issues: [],
      transcript: [],
      draft: undefined,
      requirementsSummary: undefined,
      reviewConfirmed: undefined,
      review: undefined,
    });
  };

  const onIssuesChange = (next: GithubIssueSummary[]) => {
    onChange({
      issues: next.map(issue => ({
        number: issue.number,
        title: issue.title,
        url: issue.url || undefined,
      })),
      reviewConfirmed: undefined,
    });
  };

  return (
    <div
      style={{
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {reposError ? (
        <Alert severity="error">Could not load repositories: {reposError}</Alert>
      ) : null}
      <Autocomplete<Entity, false, false, false>
        options={repos}
        value={selectedRepo}
        loading={reposLoading}
        onChange={(_event, entity) => onRepoChange(entity)}
        groupBy={entity => entity.kind}
        getOptionLabel={entity => entityLabel(entity)}
        getOptionSelected={(option, value) =>
          stringifyEntityRef(option) === stringifyEntityRef(value)
        }
        renderOption={entity => (
          <span>
            {entityLabel(entity)}{' '}
            <Typography component="span" variant="caption" color="textSecondary">
              {entity.metadata.namespace ?? 'default'}
            </Typography>
          </span>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="Repository"
            placeholder="Pick a catalog entity"
            helperText={
              repoKinds
                ? `Its GitHub issues drive the interview. Kinds: ${repoKinds.join(
                    ', ',
                  )}.`
                : 'Its GitHub issues drive the interview. Grouped by entity kind.'
            }
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {reposLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {issuesError ? (
        <Alert severity="error">Could not load issues: {issuesError}</Alert>
      ) : null}
      <Autocomplete<GithubIssueSummary, true, false, false>
        multiple
        options={issues}
        value={selectedIssueOptions}
        loading={issuesLoading}
        disabled={!formData.repoEntityRef}
        onChange={(_event, next) => onIssuesChange(next as GithubIssueSummary[])}
        getOptionLabel={issue => `#${issue.number} ${issue.title}`}
        getOptionSelected={(option, value) => option.number === value.number}
        renderTags={(value, getTagProps) =>
          value.map((issue, index) => (
            <Chip
              size="small"
              label={`#${issue.number}`}
              {...getTagProps({ index })}
              key={issue.number}
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Issues"
            placeholder={
              formData.repoEntityRef
                ? 'Select one or more issues to work'
                : 'Pick a repository first'
            }
            helperText="One or more open issues from the selected repository."
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {issuesLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <Typography variant="caption" color="textSecondary">
        Work the selected issues through the interview below. The requirements
        summary and draft it produces are saved for the repository-creation
        steps.
      </Typography>
    </div>
  );
};

function entityLabel(entity: Entity): string {
  return entity.metadata.title ?? entity.metadata.name;
}
